import {
  DOUBLE_ROOM_COMBINATIONS,
  INDIVIDUAL_ROOM_COMBINATIONS,
  QUADRUPLE_ROOM_COMBINATIONS,
  TRIPLE_ROOM_COMBINATIONS,
} from '@/constants/HotelRoomTypes';
import type { ChildBandsCount } from './types';

export type RoomTypeKey = 'individual' | 'double' | 'triple' | 'quadruple';

export const ROOM_TYPE_KEYS: RoomTypeKey[] = [
  'individual',
  'double',
  'triple',
  'quadruple',
];

export const ROOM_OPTIONS_FIELD = {
  individual: 'individualRoomOptions',
  double: 'doubleRoomOptions',
  triple: 'tripleRoomOptions',
  quadruple: 'quadrupleRoomOptions',
} as const satisfies Record<RoomTypeKey, string>;

export interface RoomComposition {
  /** 1 to 20: position in the grid block, from line 9 onwards. */
  row: number;
  roomType: RoomTypeKey;
  /** Index in the *_ROOM_COMBINATIONS constant, stored in `*RoomOptions`. */
  optionIndex: number;
  adults: number;
  /** Children of the first age band (childAges[0]). */
  younger: number;
  /** Children of the second age band (childAges[1]). */
  older: number;
}

const COMBINATIONS_BY_TYPE: Record<RoomTypeKey, number[][]> = {
  individual: INDIVIDUAL_ROOM_COMBINATIONS,
  double: DOUBLE_ROOM_COMBINATIONS,
  triple: TRIPLE_ROOM_COMBINATIONS,
  quadruple: QUADRUPLE_ROOM_COMBINATIONS,
};

/**
 * The 20 compositions in the order of the grid and of the page. The constants
 * already follow it, so row n is simply the n-th combination overall.
 */
export const ROOM_COMPOSITIONS: RoomComposition[] = ROOM_TYPE_KEYS.flatMap(
  (roomType) =>
    COMBINATIONS_BY_TYPE[roomType].map(
      ([adults, younger, older], optionIndex) => ({
        roomType,
        optionIndex,
        adults,
        younger,
        older,
      }),
    ),
).map((composition, index) => ({ ...composition, row: index + 1 }));

export const findRoomComposition = (
  roomType: RoomTypeKey,
  optionIndex: number,
) =>
  ROOM_COMPOSITIONS.find(
    (composition) =>
      composition.roomType === roomType &&
      composition.optionIndex === optionIndex,
  );

/** Same filter as getHotelRoomCombinations: what the page can offer. */
export const isCompositionAllowed = (
  { younger, older }: RoomComposition,
  childBandsCount: ChildBandsCount,
) => {
  if (childBandsCount === 0) return younger === 0 && older === 0;
  if (childBandsCount === 1) return older === 0;
  return true;
};
